import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import axiosInstance from "../../../hooks/api";
import { useFormSubmit } from "../../../hooks/useFormSubmit";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SCROLL_STYLE  = { scrollMarginTop: "100px" };

const NasDocRequestForm = () => {
  const { t } = useTranslation();
  const { isLoading, submit } = useFormSubmit();
  const [docType, setDocType] = useState("baptism");
  const [pickType, setPickType] = useState("pickup");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: "onTouched" });

  const BAPTISM      = "baptism";
  const COMMUNION    = "communion";
  const CONFIRMATION = "confirmation";
  const MARRIAGE     = "marriage";

  const sacDateLabelKey = {
    [BAPTISM]:      "_baptism_date",
    [COMMUNION]:    "_communion_date",
    [CONFIRMATION]: "_confirmation_date",
    [MARRIAGE]:     "_marriage_date",
  };

  const onSubmit = async (data) => {
    const requesterPayload = {
      requestor_name: data.requester_name,
      email:          data.requester_email,
      phone:          data.requester_phone,
      address_line_1: data.address_line_1,
      address_line_2: data.address_line_2,
      city:           data.city,
      zip:            data.zip,
      state:          data.state,
      method:         data.method === "pickup" ? "pickup" : "mail",
    };
    const isForMarriage = data.req_reason === MARRIAGE;
    const docRequestPayload = {
      baptism:         data.req_reason === BAPTISM,
      first_communion: data.req_reason === COMMUNION,
      confirmation:    data.req_reason === CONFIRMATION,
      matrimony:       data.req_reason === MARRIAGE,
      ...(isForMarriage
        ? { bride_name: data.bride_name, groom_name: data.groom_name, recipient_name: "-" }
        : {
            recipient_name:   data.child_name,
            recipient_birth:  data.recipient_birth || null,
            recipient_mother: data.mother_name,
            recipient_father: data.father_name,
          }),
      ...requesterPayload,
      recipient_relationship: data.recipient_relationship || null,
      godfather_name: data.godfather_name || null,
      godmother_name: data.godmother_name || null,
      sponsor_name:   data.sponsor_name   || null,
      saint_name:     data.saint_name     || null,
      conferred_date: data.conferred_date || null,
    };
    await submit(
      () => axiosInstance.post("/public/docRequests", { ...docRequestPayload, comments: data.comments || null }),
      {
        successText: t("_doc_request_confirmation"),
        onSuccess: () => { reset(); setDocType("baptism"); setPickType("pickup"); },
      }
    );
  };

  const inp = (name) =>
    `form-control border${errors[name] ? " is-invalid" : ""}`;

  const err = (name) =>
    errors[name] ? <div className="invalid-feedback">{errors[name].message}</div> : null;

  return (
    <div className="section pt-0">
      <style>{`
        .ndf-wrap { max-width: 1200px; margin: 0 auto; }
        .ndf-card { background: #fff; border-radius: 12px; box-shadow: 0 4px 32px rgba(3,44,101,0.13); overflow: hidden; }
        .ndf-alert { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 14px 20px; font-size: 13.5px; }
        .ndf-type-bar { background: #f8faff; border-bottom: 1px solid #e4ecf7; padding: 18px 28px; display: flex; align-items: center; gap: 20px; flex-wrap: nowrap; }
        .ndf-type-label { font-size: 11px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: #6b7a99; white-space: nowrap; flex-shrink: 0; }
        .ndf-pills { display: flex; gap: 8px; flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
        .ndf-pills::-webkit-scrollbar { display: none; }
        .ndf-pill { display: inline-flex; align-items: center; padding: 8px 20px; border-radius: 50px; border: 2px solid #032C65; font-weight: 600; font-size: 13.5px; cursor: pointer; user-select: none; white-space: nowrap; transition: background 0.18s, color 0.18s; background: #fff; color: #032C65; flex-shrink: 0; }
        .ndf-pill.active { background: #032C65; color: #fff; }
        .ndf-body { display: grid; grid-template-columns: 1fr 1fr; }
        .ndf-col { padding: 28px 28px 32px; }
        .ndf-col-left { border-right: 1px solid #e4ecf7; }
        .ndf-section { font-size: 11px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #032C65; padding-bottom: 8px; border-bottom: 2px solid #032C65; margin-bottom: 20px; margin-top: 0; }
        .ndf-section-gap { margin-top: 28px; }
        .ndf-row { display: grid; gap: 12px; margin-bottom: 14px; }
        .ndf-row-2 { grid-template-columns: 1fr 1fr; }
        .ndf-row-3 { grid-template-columns: 5fr 3fr 4fr; }
        .ndf-field { margin-bottom: 14px; }
        .ndf-row-2 .form-label { font-size: 13px; white-space: nowrap; }
        .ndf-delivery-pills { display: flex; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
        .ndf-submit { width: 100%; padding: 14px; background: #032C65; color: #fff; border: none; border-radius: 8px; font-size: 15px; font-weight: 700; letter-spacing: 0.3px; cursor: pointer; transition: background 0.2s; font-family: inherit; }
        .ndf-submit:hover:not(:disabled) { background: #021d45; }
        .ndf-submit:disabled { background: #9ca3af; cursor: not-allowed; }
        @media (max-width: 991px) {
          .ndf-body { grid-template-columns: 1fr; }
          .ndf-col-left { border-right: none; border-bottom: 1px solid #e4ecf7; }
          .ndf-type-bar { flex-wrap: wrap; gap: 12px; padding: 16px 20px; }
          .ndf-pills { flex-wrap: wrap; }
          .ndf-col { padding: 24px 24px 28px; }
        }
        @media (max-width: 575px) {
          .ndf-type-bar { padding: 14px 16px; }
          .ndf-col { padding: 20px 16px; }
          .ndf-row-2 { grid-template-columns: 1fr; }
          .ndf-row-3 { grid-template-columns: 1fr 1fr; }
          .ndf-pill { padding: 7px 16px; font-size: 13px; }
        }
      `}</style>

      <div className="container">
        <div className="section-title text-center">
          <h6 className="subtitle cursive-style">{t("_document_request_form")}</h6>
          <p>{t("_donation_message")}</p>
        </div>

        <div className="ndf-wrap">
          <div className="ndf-card">

            <div className="ndf-alert">
              <strong className="text-danger">* {t("_all_docs_provisional_copies")}</strong><br />
              <span className="text-danger">{t("_stop_donation")}</span>
            </div>

            <div className="ndf-type-bar">
              <span className="ndf-type-label">{t("_reason")}</span>
              <div className="ndf-pills">
                {[
                  { id: BAPTISM,      label: "_baptism" },
                  { id: COMMUNION,    label: "_communion" },
                  { id: CONFIRMATION, label: "_confirmation" },
                  { id: MARRIAGE,     label: "_marriage" },
                ].map((item) => (
                  <label key={item.id} htmlFor={`dt-${item.id}`} className={`ndf-pill${docType === item.id ? " active" : ""}`}>
                    <input
                      required
                      type="radio"
                      id={`dt-${item.id}`}
                      name="req_reason"
                      value={item.id}
                      onClick={() => setDocType(item.id)}
                      checked={item.id === docType}
                      style={{ display: "none" }}
                      {...register("req_reason")}
                    />
                    {t(item.label)}
                  </label>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="ndf-body">

                {/* ── LEFT ── */}
                <div className="ndf-col ndf-col-left">

                  <p className="ndf-section">{t("_section_sacrament_info")}</p>

                  {docType !== MARRIAGE ? (
                    <>
                      <div className="ndf-row ndf-row-2">
                        <div>
                          <label className="form-label fw-semibold">{t(sacDateLabelKey[docType])}</label>
                          <input type="date" className="form-control border" style={SCROLL_STYLE} {...register("conferred_date")} />
                        </div>
                        <div>
                          <label className="form-label fw-semibold">{t("_birth_date")}</label>
                          <input type="date" className="form-control border" style={SCROLL_STYLE} {...register("recipient_birth")} />
                        </div>
                      </div>

                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_child_full_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("child_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("child_name", { required: t("_err_required") })} />
                        {err("child_name")}
                      </div>

                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_mother_full_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("mother_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("mother_name", { required: t("_err_required") })} />
                        {err("mother_name")}
                      </div>

                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_father_full_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("father_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("father_name", { required: t("_err_required") })} />
                        {err("father_name")}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_marriage_date")}</label>
                        <input type="date" className="form-control border" style={SCROLL_STYLE} {...register("conferred_date")} />
                      </div>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_bride_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("bride_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("bride_name", { required: t("_err_required") })} />
                        {err("bride_name")}
                      </div>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_groom_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("groom_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("groom_name", { required: t("_err_required") })} />
                        {err("groom_name")}
                      </div>
                    </>
                  )}

                  <p className="ndf-section ndf-section-gap">{t("_section_additional_info")}</p>

                  {(docType === BAPTISM || docType === COMMUNION) && (
                    <>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_godfather_full_name")}</label>
                        <input type="text" className="form-control border" placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("godfather_name")} />
                      </div>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_godmother_full_name")}</label>
                        <input type="text" className="form-control border" placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("godmother_name")} />
                      </div>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_saint_name")}</label>
                        <input type="text" className="form-control border" placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("saint_name")} />
                      </div>
                    </>
                  )}

                  {docType === CONFIRMATION && (
                    <>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_confirmation_sponsor")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("sponsor_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("sponsor_name", { required: t("_err_required") })} />
                        {err("sponsor_name")}
                        <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>{t("_confirmation_sponsor_note")}</small>
                      </div>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_saint_name")}</label>
                        <input type="text" className="form-control border" placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("saint_name")} />
                      </div>
                    </>
                  )}

                  {docType === MARRIAGE && (
                    <>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_godfather_full_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("godfather_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("godfather_name", { required: t("_err_required") })} />
                        {err("godfather_name")}
                      </div>
                      <div className="ndf-field">
                        <label className="form-label fw-semibold">{t("_godmother_full_name")} <em className="text-danger">*</em></label>
                        <input type="text" className={inp("godmother_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                          {...register("godmother_name", { required: t("_err_required") })} />
                        {err("godmother_name")}
                      </div>
                    </>
                  )}
                </div>

                {/* ── RIGHT ── */}
                <div className="ndf-col">

                  <p className="ndf-section">{t("_section_contact_info")}</p>

                  <div className="ndf-field">
                    <label className="form-label fw-semibold">{t("_requester_name")} <em className="text-danger">*</em></label>
                    <input type="text" className={inp("requester_name")} placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                      {...register("requester_name", { required: t("_err_required") })} />
                    {err("requester_name")}
                  </div>

                  <div className="ndf-field">
                    <label className="form-label fw-semibold">{t("_relation")}</label>
                    <input type="text" className="form-control border" placeholder={t("_name_placeholder")} style={SCROLL_STYLE}
                      {...register("recipient_relationship")} />
                  </div>

                  <div className="ndf-row ndf-row-2">
                    <div>
                      <label className="form-label fw-semibold">{t("_requester_phone")} <em className="text-danger">*</em></label>
                      <input type="tel" className={inp("requester_phone")} placeholder="212-395-9280" style={SCROLL_STYLE}
                        {...register("requester_phone", { required: t("_err_required") })} />
                      {err("requester_phone")}
                    </div>
                    <div>
                      <label className="form-label fw-semibold">{t("_email")} <em className="text-danger">*</em></label>
                      <input type="email" className={inp("requester_email")} placeholder="email@example.com" style={SCROLL_STYLE}
                        {...register("requester_email", {
                          required: t("_err_required"),
                          pattern: { value: EMAIL_PATTERN, message: t("_err_invalid_email") },
                        })} />
                      {err("requester_email")}
                    </div>
                  </div>

                  <div className="ndf-field">
                    <label className="form-label fw-semibold">{t("_delivery_method")} <em className="text-danger">*</em></label>
                    <div className="ndf-delivery-pills">
                      {[
                        { id: "pickup", label: "_pickup" },
                        { id: "mail",   label: "_mail" },
                      ].map((item) => (
                        <label key={item.id} htmlFor={`dm-${item.id}`} className={`ndf-pill${pickType === item.id ? " active" : ""}`}>
                          <input
                            required
                            type="radio"
                            id={`dm-${item.id}`}
                            name="pick_type"
                            value={item.id}
                            onClick={() => setPickType(item.id)}
                            checked={item.id === pickType}
                            style={{ display: "none" }}
                            {...register("method")}
                          />
                          {t(item.label)}
                        </label>
                      ))}
                    </div>
                  </div>

                  <p className="ndf-section ndf-section-gap">{t("_section_home_address")}</p>

                  <div className="ndf-field">
                    <label className="form-label fw-semibold">{t("_address_1")} <em className="text-danger">*</em></label>
                    <input type="text" className={inp("address_line_1")} placeholder="Street address" style={SCROLL_STYLE}
                      {...register("address_line_1", { required: t("_err_required") })} />
                    {err("address_line_1")}
                  </div>

                  <div className="ndf-field">
                    <label className="form-label fw-semibold">{t("_address_2")}</label>
                    <input type="text" className="form-control border" placeholder="Apt, Suite, Unit..." style={SCROLL_STYLE}
                      {...register("address_line_2")} />
                  </div>

                  <div className="ndf-row ndf-row-3">
                    <div>
                      <label className="form-label fw-semibold">{t("_city")} <em className="text-danger">*</em></label>
                      <input type="text" className={inp("city")} style={SCROLL_STYLE}
                        {...register("city", { required: t("_err_required") })} />
                      {err("city")}
                    </div>
                    <div>
                      <label className="form-label fw-semibold">{t("_state")} <em className="text-danger">*</em></label>
                      <input type="text" className={inp("state")} maxLength={2} style={{ ...SCROLL_STYLE, textTransform: "uppercase" }}
                        {...register("state", { required: t("_err_required") })} />
                      {err("state")}
                    </div>
                    <div>
                      <label className="form-label fw-semibold">{t("_zip")} <em className="text-danger">*</em></label>
                      <input type="text" className={inp("zip")} style={SCROLL_STYLE}
                        {...register("zip", { required: t("_err_required") })} />
                      {err("zip")}
                    </div>
                  </div>

                  <div className="ndf-field" style={{ marginTop: "4px" }}>
                    <label className="form-label fw-semibold">{t("_comments")}</label>
                    <textarea className="form-control border" rows={3} {...register("comments")} />
                  </div>

                  <div style={{ marginTop: "20px" }}>
                    <button type="submit" className="ndf-submit" disabled={isLoading}>
                      {isLoading ? "..." : t("_place_request")}
                    </button>
                  </div>

                </div>

              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default NasDocRequestForm;
